import { profileFetch } from "../profileSession";
import { API_BASE, getToken, downloadApiFile, ApiError, req } from "./transport";
import type { DisputeProposal, Dispute, DisputeProposalInput, DisputeProposalReview, DisputeCase } from "./types";

export const disputesApi = {
  // [disputes.original-parties](../../../../docs/product/disputes.md#disputes-original-parties) / [disputes.version-consent](../../../../docs/product/disputes.md#disputes-version-consent): profile-scoped cases; settlement requires both original parties' consent.
  async openDispute(payload: { dealType: "order" | "contract"; dealId: string; reason: string; message?: string }) {
    return req<{ dispute: Dispute }>("/disputes", { method: "POST", body: JSON.stringify(payload) });
  },

  async myDisputes(signal?: AbortSignal) {
    const r = await req<{ disputes: Dispute[] }>("/disputes/mine", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.disputes)) throw new Error("Missing disputes.");
    return r?.disputes ?? [];
  },

  async adminDisputeQueue(activeOnly = true, page = 0) {
    return req<{ disputes: Dispute[]; hasMore: boolean }>(`/disputes/admin/queue?active=${activeOnly}&page=${page}`, {}, { rejectUnauthorized: true });
  },

  async disputeCase(id: string, before?: number) {
    return req<DisputeCase>(`/disputes/${encodeURIComponent(id)}${before ? `?before=${before}` : ""}`, {}, { rejectUnauthorized: true });
  },

  async disputeForDeal(dealType: "order" | "contract", dealId: string) {
    return req<{ dispute: Dispute }>(`/disputes/by-deal/${dealType}/${encodeURIComponent(dealId)}`);
  },

  async disputeAction(id: string, action: "messages" | "read" | "join" | "help", payload: { message?: string; sequence?: number } = {}) {
    return req<{ ok: boolean }>(`/disputes/${encodeURIComponent(id)}/${action}`, { method: "POST", body: JSON.stringify(payload) }, { rejectUnauthorized: true });
  },

  async sendDisputeMessage(id: string, message: string, image?: File | null): Promise<void> {
    if (!image) { await disputesApi.disputeAction(id, "messages", { message }); return; }
    const form = new FormData();
    form.append("message", message); form.append("image", image);
    const token = getToken();
    const response = await profileFetch(`${API_BASE}/disputes/${encodeURIComponent(id)}/messages`, {
      method: "POST", credentials: "include", body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const failure: { error?: string; code?: string } = await response.json().catch(() => ({}));
      throw new ApiError(response.status, failure.error || "Could not send the image. Please try again.", failure.code);
    }
  },

  async disputeMessageImage(id: string, messageId: string, signal: AbortSignal) {
    const token = getToken();
    const response = await profileFetch(`${API_BASE}/disputes/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/image`, {
      credentials: "include", signal, headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const failure: { error?: string; code?: string } = await response.json().catch(() => ({}));
      throw new ApiError(response.status, failure.error || "Could not load the image.", failure.code);
    }
    return response.blob();
  },

  async previewDisputeProposal(id: string, payload: DisputeProposalInput) {
    return req<{ review: DisputeProposalReview }>(`/disputes/${encodeURIComponent(id)}/proposals/preview`, { method: "POST", body: JSON.stringify(payload) }, { rejectUnauthorized: true });
  },

  async proposeDispute(id: string, payload: DisputeProposalInput & { reviewedTerms?: DisputeProposalReview }) {
    return req<{ ok: boolean }>(`/disputes/${encodeURIComponent(id)}/proposals`, { method: "POST", body: JSON.stringify(payload) }, { rejectUnauthorized: true });
  },

  async decideDisputeProposal(id: string, proposalId: string, version: number, action: "accept" | "decline", expectedDocumentId?: string) {
    return req<{ ok: boolean }>(`/disputes/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalId)}/${action}`, { method: "POST", body: JSON.stringify({ version,expectedDocumentId }) }, { rejectUnauthorized: true });
  },

  async disputeProposalHistory(id:string,before?:number) {
    return req<{proposals:DisputeProposal[];hasMore:boolean}>(`/disputes/${encodeURIComponent(id)}/proposals${before ? `?before=${before}` : ""}`,undefined,{rejectUnauthorized:true});
  },

  async requestDisputeContinuation(id: string, requestId: string) {
    return req<{ dispute: Dispute }>(`/disputes/${encodeURIComponent(id)}/continuations`, { method: "POST", body: JSON.stringify({ requestId }) }, { rejectUnauthorized: true });
  },

  async decideDisputeContinuation(id: string, requestId: string, action: "accept" | "decline") {
    return req<{ dispute: Dispute }>(`/disputes/${encodeURIComponent(id)}/continuations/${encodeURIComponent(requestId)}/${action}`, { method: "POST", body: JSON.stringify({}) }, { rejectUnauthorized: true });
  },

  async withdrawDispute(id: string) {
    return req<{ dispute: Dispute }>(`/disputes/${encodeURIComponent(id)}/withdraw`, { method: "POST", body: JSON.stringify({}) }, { rejectUnauthorized: true });
  },

  async markDisputeUnresolved(id: string, reason: string) {
    return req<{ dispute: Dispute }>(`/disputes/${encodeURIComponent(id)}/unresolved`, { method: "POST", body: JSON.stringify({ reason }) }, { rejectUnauthorized: true });
  },

  async closeDispute(id: string, note: string, bothPartiesConfirmed: true) {
    return req<{ dispute: Dispute }>(`/disputes/${encodeURIComponent(id)}/close`, { method: "POST", body: JSON.stringify({ note, bothPartiesConfirmed }) }, { rejectUnauthorized: true });
  },

  async downloadDisputeDocument(id: string, kind: "agreement" | "case-export" | "settlement", proposalId?:string) {
    const caseExport = kind === "case-export";
    return downloadApiFile(
      `/disputes/${encodeURIComponent(id)}/${caseExport ? "export.zip" : kind === "settlement" ? `proposals/${encodeURIComponent(proposalId || "")}/settlement.pdf` : "agreement.pdf"}`,
      () => caseExport ? `case-${id}.zip` : kind === "settlement" ? `settlement-${id}-${proposalId}.pdf` : `agreement-${id}.pdf`,
      caseExport ? "Could not export the case records. Please try again." : "Could not download the agreement.",
      { serverErrors: true },
    );
  }
};
